import { apolloClient } from './graphql_client';
import { gql } from '@apollo/client';

const SEND_PASSWORD_RESET_EMAIL = gql`
  mutation SendPasswordResetEmail($username: String!) {
    sendPasswordResetEmail(input: { username: $username }) {
      success
    }
  }
`;

const RESET_USER_PASSWORD = gql`
  mutation ResetUserPassword($key: String!, $login: String!, $password: String!) {
    resetUserPassword(input: { key: $key, login: $login, password: $password }) {
      user {
        id
        databaseId
        email
      }
    }
  }
`;

interface SendPasswordResetEmailData {
  sendPasswordResetEmail?: {
    success?: boolean | null;
  } | null;
}

interface ResetUserPasswordData {
  resetUserPassword?: {
    user?: {
      id?: string;
      databaseId?: number;
      email?: string;
    } | null;
  } | null;
}

type MutationErrorLike = {
  message?: string;
};

/**
 * Request a WordPress password-reset email for username or email.
 * Always treat as success from UX side (WP intentionally doesn't reveal if user exists).
 */
export const sendPasswordResetEmail = async (usernameOrEmail: string): Promise<void> => {
  try {
    await apolloClient.mutate<SendPasswordResetEmailData>({
      mutation: SEND_PASSWORD_RESET_EMAIL,
      variables: { username: usernameOrEmail.trim() },
    });
  } catch (error: unknown) {
    const err = error as {
      message?: string;
      graphQLErrors?: MutationErrorLike[];
      networkError?: { message?: string };
    };
    const first =
      err?.graphQLErrors?.[0]?.message ||
      err?.networkError?.message ||
      err?.message ||
      '';
    const lower = first.toLowerCase();
    if (
      lower.includes('failed to fetch') ||
      lower.includes('network') ||
      lower.includes('cors') ||
      lower.includes('internal server')
    ) {
      throw new Error('Unable to send reset email. Please try again later.');
    }
    // WP may return soft errors for unknown users; still show a generic success upstream.
  }
};

/**
 * Set a new password using the key from the reset email.
 */
export const resetUserPassword = async (
  login: string,
  key: string,
  password: string
): Promise<void> => {
  try {
    const result = await apolloClient.mutate<ResetUserPasswordData>({
      mutation: RESET_USER_PASSWORD,
      variables: {
        login: login.trim(),
        key: key.trim(),
        password,
      },
    });

    if (!result.data?.resetUserPassword?.user) {
      throw new Error('Unable to reset password. Please request a new reset link.');
    }
  } catch (error: unknown) {
    if (error instanceof Error && error.message.includes('Unable to reset password')) {
      throw error;
    }
    const err = error as {
      message?: string;
      graphQLErrors?: MutationErrorLike[];
    };
    throw new Error(
      err?.graphQLErrors?.[0]?.message ||
        err?.message ||
        'Unable to reset password. The link may have expired.'
    );
  }
};
